import { useState } from 'react';

import {
  deleteSiteCmsDocumentApi,
  downloadSiteCmsDocumentApi,
  renameSiteCmsDocumentApi,
  uploadSiteCmsDocumentApi,
} from '@/lib/api/siteCmsApi';
import { cmsDocumentReferences } from '@shared/cms/content';
import { cmsResourceLocations } from '@shared/cms/resource-usage';
import type { SiteCmsDocumentResponse, SiteCmsDraftResponse } from '@shared/types/entities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { SiteCmsDialog } from './SiteCmsDialog';
import SiteCmsStorageQuota from './SiteCmsStorageQuota';
import { nextCmsDocumentSlug } from './site-cms-editor-utils';
import { cmsSearchMatches } from './site-cms-interaction-model';
import { cmsBytes, cmsStorageFileIssue } from './site-cms-storage-model';
import { useCmsBusyOperation, useCmsInteractions, useCmsPendingForm } from './use-cms-interactions';
import { cmsMediaUsageKey, useCmsMediaUsage } from './use-cms-media-usage';

type Props = {
  draft: SiteCmsDraftResponse;
  documents: SiteCmsDocumentResponse[];
  failed: boolean;
  loading?: boolean;
  retry: () => void;
  onMetadataChanged: () => void;
};
export default function SiteCmsDocuments({ draft, documents, failed, loading, retry, onMetadataChanged }: Props) {
  const { ownerId, ownerType } = draft;
  const queryClient = useQueryClient();
  const { ask } = useCmsInteractions();
  const queryKey = ['site-cms', 'documents', ownerType, ownerId];
  const usage = useCmsMediaUsage(ownerType, ownerId);
  const quota = usage.data?.documents;
  const quotaUnavailable = usage.isError || !quota;
  const refreshUsage = () => {
    void queryClient.invalidateQueries({ queryKey: cmsMediaUsageKey(ownerType, ownerId) });
  };
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SiteCmsDocumentResponse | null>(null);
  const [failure, setFailure] = useState('');
  useCmsPendingForm('pdf-upload', Boolean(title || file));
  useCmsPendingForm('pdf-title', Boolean(editing && editing.title !== documents.find(d => d.id === editing.id)?.title));
  const upload = useMutation({
    mutationFn: (value: { title: string; file: File }) =>
      uploadSiteCmsDocumentApi(
        ownerType,
        ownerId,
        nextCmsDocumentSlug(value.title, documents),
        value.title,
        value.file,
      ),
    onSuccess: doc => {
      queryClient.setQueryData<SiteCmsDocumentResponse[]>(queryKey, rows => [
        doc,
        ...(rows ?? []).filter(d => d.id !== doc.id),
      ]);
      setTitle('');
      setFile(null);
      setInputKey(v => v + 1);
      setFailure('');
      toast.success('PDF ajouté à la bibliothèque privée.');
    },
    onError: error => setFailure(error.message),
    onSettled: refreshUsage,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteSiteCmsDocumentApi(ownerType, ownerId, id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<SiteCmsDocumentResponse[]>(queryKey, rows => (rows ?? []).filter(row => row.id !== id));
      setFailure('');
    },
    onError: error => setFailure(error.message),
    onSettled: refreshUsage,
  });
  const rename = useMutation({
    mutationFn: (doc: SiteCmsDocumentResponse) =>
      renameSiteCmsDocumentApi(ownerType, ownerId, doc.id, doc.title.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      onMetadataChanged();
      setEditing(null);
      setFailure('');
      toast.success('Titre enregistré dans la bibliothèque. Le site public n’a pas changé.');
    },
    onError: error => setFailure(error.message),
    onSettled: refreshUsage,
  });
  useCmsBusyOperation('document-library', upload.isPending || remove.isPending || rename.isPending);
  async function download(doc: SiteCmsDocumentResponse) {
    try {
      const blob = await downloadSiteCmsDocumentApi(ownerType, ownerId, doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Téléchargement impossible');
    }
  }
  async function close() {
    if (rename.isPending) return;
    if (
      editing &&
      editing.title !== documents.find(d => d.id === editing.id)?.title &&
      (await ask({
        title: 'Abandonner ce titre ?',
        message: 'Le nouveau titre n’a pas encore été enregistré.',
        choices: [{ value: 'close', label: 'Abandonner', danger: true }],
      })) !== 'close'
    )
      return;
    setEditing(null);
  }
  const fileIssue = cmsStorageFileIssue('documents', file, quota);
  const refs = cmsDocumentReferences(draft.pages, draft.headerMenu);
  const results = documents.filter(d => cmsSearchMatches(`${d.title} ${d.fileName}`, search));
  return (
    <div className='cms-stack'>
      <div className='cms-notice'>
        Le choix d’un PDF dans une section, le menu ou un lien de page l’inclut dans les fichiers de la prochaine
        publication. Importer seul ne le publie pas.
      </div>
      {failure && (
        <div className='cms-alert' role='alert'>
          {failure}
        </div>
      )}
      <SiteCmsStorageQuota
        kind='documents'
        quota={quota}
        loading={usage.isPending}
        failed={usage.isError}
        refreshing={usage.isFetching}
        retry={() => void usage.refetch()}
      />
      <section className='cms-card'>
        <div className='cms-card-heading'>
          <h3>Documents PDF</h3>
          <Upload size={22} />
        </div>
        <details className='cms-import-details'>
          <summary>Ajouter un document</summary>
          <fieldset disabled={upload.isPending || failed || loading || quotaUnavailable} className='cms-upload-form'>
            <label>
              Titre du document
              <input
                aria-label='Titre du document PDF'
                maxLength={150}
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </label>
            <label>
              Fichier PDF
              <input
                key={inputKey}
                aria-label='Fichier PDF privé'
                type='file'
                accept='application/pdf,.pdf'
                onChange={e => {
                  const source = e.target.files?.[0] ?? null;
                  setFile(source);
                  if (source && !title) setTitle(source.name.replace(/\.pdf$/i, '').slice(0, 150));
                }}
              />
            </label>
            <button
              type='button'
              className='cms-button cms-button-primary'
              disabled={
                !title.trim() ||
                !file ||
                Boolean(fileIssue) ||
                quotaUnavailable ||
                documents.length >= (quota?.maxCount ?? 60)
              }
              onClick={() => {
                if (!file) return;
                if (fileIssue || quotaUnavailable) {
                  setFailure(fileIssue ?? 'Vérifiez le quota avant l’import.');
                  return;
                }
                upload.mutate({ title: title.trim(), file });
              }}
            >
              {upload.isPending ? 'Envoi…' : 'Ajouter le PDF'}
            </button>
          </fieldset>
          {fileIssue && (
            <p className='cms-alert' role='alert'>
              {fileIssue}
            </p>
          )}
          {(file || title) && (
            <button
              type='button'
              className='cms-link-button'
              disabled={upload.isPending}
              onClick={() => {
                setFile(null);
                setTitle('');
                setInputKey(value => value + 1);
              }}
            >
              Annuler cet import
            </button>
          )}
        </details>
        {file && (
          <p className='cms-hint'>
            Fichier sélectionné : {cmsBytes(file.size)}
            {quota ? ` · après import : ${cmsBytes(quota.usedBytes + file.size)} / ${cmsBytes(quota.maxBytes)}` : ''}
          </p>
        )}
      </section>
      <section className='cms-card'>
        <div className='cms-card-heading'>
          <h3>Votre bibliothèque</h3>
          <input
            aria-label='Rechercher un document'
            value={search}
            placeholder='Rechercher…'
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {loading ? (
          <p role='status'>Chargement des documents…</p>
        ) : failed ? (
          <p role='alert' className='cms-alert'>
            Bibliothèque indisponible.{' '}
            <button type='button' onClick={retry}>
              Réessayer
            </button>
          </p>
        ) : (
          <>
            <div className='cms-document-list'>
              {results.map(doc => (
                <div className='cms-document-row' key={doc.id}>
                  <FileText size={24} />
                  <div className='cms-document-info'>
                    <h4>{doc.title}</h4>
                    <small>
                      {doc.fileName} · {Math.ceil(doc.sizeBytes / 1024)} Ko
                    </small>
                    <span className='cms-pill'>
                      {refs.has(doc.slug)
                        ? 'Dans le travail en cours'
                        : doc.usedInDraft
                          ? 'Dans le brouillon enregistré'
                          : doc.usedInLive
                            ? 'En ligne'
                            : doc.retainedInPublication
                              ? 'Conservé dans une version'
                              : 'Bibliothèque uniquement'}
                    </span>
                  </div>
                  <div className='cms-media-actions'>
                    <button type='button' className='cms-button' onClick={() => setEditing(doc)}>
                      Détails & titre
                    </button>
                    <button
                      type='button'
                      className='cms-icon-button'
                      aria-label={`Télécharger ${doc.title}`}
                      onClick={() => void download(doc)}
                    >
                      <Download size={17} />
                    </button>
                    <button
                      type='button'
                      className='cms-icon-button cms-danger'
                      aria-label={`Supprimer ${doc.title}`}
                      disabled={refs.has(doc.slug) || doc.usedInDraft || doc.retainedInPublication || remove.isPending}
                      onClick={async () => {
                        if (
                          (await ask({
                            title: `Supprimer « ${doc.title} » ?`,
                            message:
                              'Le fichier sera retiré de la bibliothèque privée. Les fichiers conservés dans les versions ne peuvent pas être supprimés ici.',
                            choices: [{ value: 'delete', label: 'Supprimer le document', danger: true }],
                          })) === 'delete'
                        )
                          remove.mutate(doc.id);
                      }}
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {!results.length && (
              <div className='cms-empty'>
                <FileText size={30} />
                <h4>{documents.length ? 'Aucun résultat' : 'Aucun document pour le moment'}</h4>
                <p>
                  {documents.length
                    ? 'Modifiez votre recherche.'
                    : 'Ajoutez un règlement, un calendrier ou une information pratique.'}
                </p>
              </div>
            )}
          </>
        )}
        <p className='cms-hint'>
          Une version conservée peut retenir ce fichier pour la restauration. Le retrait d’un lien ne supprime pas les
          anciennes publications.
        </p>
      </section>
      {editing && (
        <SiteCmsDialog title='Document : titre et usages' onClose={() => void close()} busy={rename.isPending}>
          <fieldset disabled={rename.isPending} className='cms-edit-fields'>
            <label>
              Titre du document
              <input
                value={editing.title}
                maxLength={150}
                onChange={e => setEditing({ ...editing, title: e.target.value })}
              />
            </label>
            <p className='cms-hint'>
              Le titre est enregistré dans la bibliothèque. Une nouvelle publication est nécessaire pour actualiser le
              site public.
            </p>
            <button
              type='button'
              className='cms-button cms-button-primary'
              disabled={!editing.title.trim() || rename.isPending}
              onClick={() => rename.mutate(editing)}
            >
              Enregistrer le titre
            </button>
            <h3>Utilisations</h3>
            <ul>
              {cmsResourceLocations('document', editing.slug, draft.pages, draft.headerMenu).map(label => (
                <li key={label}>Travail en cours · {label}</li>
              ))}
              {(editing.usages ?? []).map((u, index) => (
                <li key={index}>
                  {u.scope === 'saved'
                    ? 'Brouillon enregistré'
                    : u.scope === 'live'
                      ? 'Version en ligne'
                      : `Version ${u.revision} conservée`}{' '}
                  · {u.label}
                </li>
              ))}
            </ul>
            <h4>Référence interne</h4>
            <code>/documents/{editing.slug}.pdf</code>
            <p>
              Cette référence n’est pas une preuve de disponibilité publique. Utilisez « Choisir un lien » depuis une
              page pour la sélectionner et confirmer sa diffusion.
            </p>
            {failure && (
              <p role='alert' className='cms-alert'>
                {failure}
              </p>
            )}
          </fieldset>
        </SiteCmsDialog>
      )}
    </div>
  );
}
